import type {Schemas} from "api/schemaAssert";
import type {OptionKey} from "home/components/createFormLogic";
import type {UpsellCardModel} from "home/components/upsellCard";
import type {UpgradeMomentModel} from "./upgradeMoments";
import type {OfferDetail} from "./offerEvents";
import {planCodeForId} from "pricing/planCatalogue";

/**
 * WHICH wall armed the send screen's upsell card, as the funnel records it
 * (kitecorp/transferify-api-service#1777, ui#2555).
 *
 * React-free by the repo's logic-outside-react mandate, and a module of its own rather than a ternary
 * inside the card's hook: it is a translation between two closed vocabularies — the browser's card model
 * and the api's two columns — and a translation that cannot be exercised without rendering a card is one
 * nobody can check against literals.
 *
 * <b>TWO fields, not one composite.</b> `trigger` holds the KIND of wall and `triggerOption` the locked
 * control an `OPTION` wall named, because the dashboard asks two different questions — which kind of wall
 * converts, and which locked control converts — and each is one `GROUP BY` over one column that way. The
 * api enforces the pairing itself (`UpsellCardWall.isComplete`: required on `UPSELL_CARD`, refused
 * everywhere else, and the option key present exactly when the trigger is `OPTION`), so a report that
 * breaks it is a 400 rather than a row nobody can read.
 *
 * <b>The spellings here ARE the wire.</b> They are the api's own enum NAMES, bound below to the generated
 * schema, so a renamed value fails `tsc` naming the field instead of silently emptying a published panel.
 */

/** The kind of wall, spelled as `UpsellCardTrigger`. */
export type UpsellCardTrigger = NonNullable<Schemas["UpsellOfferEventRequest"]["trigger"]>;

/** The locked control an `OPTION` wall named, spelled as `UpsellCardOption`. */
export type UpsellCardOptionKey = NonNullable<Schemas["UpsellOfferEventRequest"]["triggerOption"]>;

/** One wall, ready for the wire: the discriminant, and the control where the discriminant demands one. */
export interface UpsellCardWall {
	trigger: UpsellCardTrigger;
	triggerOption?: UpsellCardOptionKey;
}

/**
 * The wire spelling of each locked option row, or null for a row that must not be reported as one.
 *
 * A total record rather than a lookup with a fallback: a control added to {@link OptionKey} fails `tsc`
 * here, which is the whole point — an unmapped row would otherwise be reported as an `OPTION` wall with
 * no control named, and the api refuses that pair with a 400 the browser never sees (the beacon is
 * fire-and-forget), so the wall would simply stop being counted.
 *
 * `password` is null because the CARD gives the password row its own discriminant: admitting it in both
 * places would give one control two spellings and split a panel line in half. The api refuses it as an
 * option key from its own side.
 */
const WIRE_OPTION_KEYS: Record<OptionKey, UpsellCardOptionKey | null> = {
	password: null,
	dlimit: "DLIMIT",
	zeroKnowledge: "ZERO_KNOWLEDGE",
	notify: "NOTIFY",
	recovery: "RECOVERY",
};

/**
 * The wall this card is selling past, or null where it cannot be named on the wire.
 *
 * Null means DO NOT SEND — not "send without a wall". `UPSELL_CARD` requires one, so a report with none
 * is refused, and a report with a wall this build cannot spell would be refused too; dropping the step is
 * the honest failure, and the funnel is a floor rather than a census by contract.
 */
export function upsellCardWall(model: UpsellCardModel): UpsellCardWall | null {
	switch (model.trigger) {
		case "password":
			return {trigger: "PASSWORD"};
		case "expiry":
			return {trigger: "EXPIRY"};
		case "maxExpiry":
			return {trigger: "MAX_EXPIRY"};
		case "option": {
			const triggerOption = WIRE_OPTION_KEYS[model.option];
			return triggerOption === null ? null : {trigger: "OPTION", triggerOption};
		}
	}
}

/**
 * The wall an upgrade moment arms the same card with (kitecorp/transferify-ui#2909,
 * kitecorp/transferify-api-service#2169), so its test group's surface is recorded before checkout.
 *
 * Null for the held moment: the api has no name for it until the owner switches it on, and a report the
 * api refuses is a step lost, so it must not be sent. Unreachable while it is held.
 */
export function upgradeMomentWall(model: UpgradeMomentModel): UpsellCardWall | null {
	switch (model.moment) {
		case "bigFiles":
			return {trigger: "BIG_FILES"};
		case "repeatRecipient":
			return {trigger: "REPEAT_RECIPIENT"};
		case "unpaidLimit":
			return {trigger: "UNPAID_LIMIT"};
		case "sendingDays":
			return null;
	}
}

/**
 * Everything the card's funnel rows carry beside the moment: the wall, and the tier the card SELLS
 * (kitecorp/transferify-api-service#1794), which is the tier its own copy names. The locked-control card
 * wins over an upgrade moment, exactly as the panel renders it.
 *
 * Null means DO NOT SEND, for {@link upsellCardWall}'s reason: without a wall the api refuses the card.
 */
export function upsellCardDetail(
	card: UpsellCardModel | null, moment: UpgradeMomentModel | null,
): OfferDetail | null {
	if (card !== null) {
		const wall = upsellCardWall(card);
		return wall === null ? null : {wall, targetPlan: planCodeForId(card.target)};
	}
	if (moment === null || moment.moment === "sendingDays") {
		return null;
	}
	const wall = upgradeMomentWall(moment);
	return wall === null ? null : {wall, targetPlan: planCodeForId(moment.target)};
}
